import {
  PaymentMethodType as VoPaymentMethodType,
  PaymentMethod as VoPaymentMethod,
  SupportedCurrencies,
  VerificationStatus,
  CardBrand,
  formatPhoneNumber,
} from 'vopay';

import { useState } from 'react';
import { useApi } from '#shared/providers/Api';

export type AddInteracParams = {
  question: string;
  answer: string;
  email: string;
  phone: string;
  recipientName: string;
  accountID?: string;
};

export type PaymentMethodType = VoPaymentMethodType;
type Kind = VoPaymentMethod['ExtraFields']['RecipientType'] | 'HOLDER';

export type PaymentMethod = {
  type: PaymentMethodType;
  accountHolderName?: string;
  typeLabel: string;
  kind?: Kind;
  groupedItems?: PaymentMethod[];
  addedOn: string;
  details: string;
  isDefault: boolean;
  token: string;
  currency: SupportedCurrencies | null;
  verificationStatus?: VerificationStatus;
  institutionName?: string;
  creditCardBrand?: CardBrand;
  expirationDate?: string;
  bankId?: string;
  verificationSentOn?: string;
  hasQuestionAndAnswer?: boolean;
  paymentRails?: string;
};

type HookProps = {
  accountID?: string;
  currencies?: SupportedCurrencies[];
  // Host facts the portal read from its ClientData provider; shared takes them
  // as options instead.
  clientAccountsEnabled: boolean;
  // The signed-in portal user, sent as PortalUser by several endpoints
  // (delete bank account / credit card, set default bank account, add Interac).
  loginUserName?: string;
};

const creditCardMap: Record<CardBrand, string> = {
  amex: 'Amex',
  visa: 'Visa',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  uatp: 'UATP',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  unknown: 'Card',
};

export default function usePaymentMethods({
  accountID,
  currencies = ['CAD'],
  clientAccountsEnabled,
  loginUserName = '',
}: HookProps) {
  const api = useApi();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [groupedMethods, setGroupedMethods] = useState<PaymentMethod[]>([]);
  const CUSTOMER_ID_PROP = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';

  const deleteMethod = (method: Pick<PaymentMethod, 'type' | 'token'>, _accountID = accountID) => {
    if (!_accountID) {
      return {
        Success: false,
        ErrorMessage: 'Account ID is required',
      };
    }

    if (method.type === 'PayPal') {
      return deletePaypal(method.token);
    }

    if (method.type === 'Venmo') {
      return deleteVenmo(method.token);
    }

    if (method.type === 'Zelle') {
      return deleteZelle(method.token);
    }

    if (method.type === 'BankAccount') {
      return deleteBankAccount(method.token);
    }

    if (method.type === 'Email' || method.type === 'PhoneNumber') {
      return deleteInterac(method.token, _accountID);
    }

    if (method.type === 'CreditCard') {
      return deleteCreditCard(method.token);
    }

    if (method.type === 'DebitCard') {
      return deleteDebitCard(method.token);
    }

    return {
      Success: false,
      ErrorMessage: 'Invalid payment method',
    };
  };

  const deleteGroupedMethod = async (method: PaymentMethod, _accountID = accountID) => {
    if (!_accountID) {
      return [
        {
          Success: false,
          ErrorMessage: 'Account ID is required',
        },
      ];
    }

    const deletePromises = [];

    if (method.kind !== 'HOLDER') {
      deletePromises.push(deleteMethod(method, _accountID));
    }

    if (method.groupedItems) {
      for (const groupedItem of method.groupedItems) {
        deletePromises.push(deleteMethod(groupedItem, _accountID));
      }
    }

    try {
      const results = await Promise.all(deletePromises);

      return results;
    } catch (error) {
      console.error('Error deleting grouped methods:', error);

      return [
        {
          Success: false,
          ErrorMessage: 'Unexpected error while deleting grouped methods',
        },
      ];
    }
  };

  function deleteBankAccount(Token: string) {
    return api.postDeleteClientBankAccount({
      PortalUser: loginUserName,
      Token,
    });
  }

  function deletePaypal(PayPalToken: string) {
    return api.postDeletePaypalAccount({ PayPalToken });
  }

  function deleteVenmo(VenmoToken: string) {
    return api.postDeleteVenmoAccount({ VenmoToken });
  }

  function deleteZelle(ZelleToken: string) {
    return api.deleteZelleAccount({ ZelleToken });
  }

  function deleteInterac(Token: string, _accountID: string) {
    // TODO: b/e to accept ContactID as well
    return api.postDeleteInteracEmail({ Token, ClientAccountID: _accountID });
  }

  function deleteCreditCard(CreditCardToken: string) {
    return api.postDeleteClientCreditCard({
      PortalUser: loginUserName,
      CreditCardToken,
    });
  }

  function deleteDebitCard(CardToken: string) {
    return api.postDeleteDebitCard({ CardToken });
  }

  const [fetching, setFetching] = useState(false);

  const fetchMethods = async (_accountID = accountID) => {
    try {
      if (!_accountID) {
        throw new Error('Account ID is required');
      }

      setFetching(true);

      const res = clientAccountsEnabled
        ? await api.fetchClientAccountPaymentMethods({
            ClientAccountID: _accountID,
          })
        : await api.fetchContactPaymentMethods({
            ContactID: _accountID,
          });

      if (!res.Success) {
        throw new Error(`Couldn't fetch payment methods for ${_accountID}`);
      }

      const accountLabels: Record<PaymentMethodType, string> = {
        BankAccount: 'Bank Account',
        CreditCard: 'Credit Card',
        DebitCard: 'Debit Card',
        Email: 'Interac e-Transfer',
        PhoneNumber: 'Interac e-Transfer',
        GooglePay: 'Google Pay',
        ApplePay: 'Apple Pay',
        PayPal: 'PayPal',
        Venmo: 'Venmo',
        Zelle: 'Zelle',
      };

      const getAccountHolderName = (m: VoPaymentMethod) => {
        if (m.PaymentMethod === 'BankAccount') return m.ExtraFields.AccountHolderName;

        if (['CreditCard', 'ApplePay', 'GooglePay'].includes(m.PaymentMethod)) return m.ExtraFields.CardHolderName;

        if (['Email', 'PhoneNumber', 'PayPal', 'Venmo', 'Zelle'].includes(m.PaymentMethod)) return m.ExtraFields.Name;

        return '';
      };

      const paymentMethods = res.PaymentMethods.map((m) => {
        return {
          accountHolderName: getAccountHolderName(m),
          type: m.PaymentMethod,
          typeLabel: accountLabels[m.PaymentMethod],
          // Zelle exposes its alias via AliasType; PayPal/Venmo use RecipientType.
          kind: m.ExtraFields?.RecipientType ?? m.ExtraFields?.AliasType,
          addedOn: m.AddedOn,
          details: m.PaymentDetails || '',
          isDefault: m.IsDefault,
          token: m.Token,
          currency: m.Currency,
          verificationStatus: m.ExtraFields?.VerificationStatus,
          institutionName: m.ExtraFields?.InstitutionName,
          bankId: m.ExtraFields?.BankID,
          creditCardBrand: m.ExtraFields?.CardBrand,
          expirationDate: m.ExtraFields?.ExpirationDate,
          verificationSentOn: m.ExtraFields?.VerificationSentOn,
          hasQuestionAndAnswer: m.ExtraFields?.HasQuestionAndAnswer,
          paymentRails: m.ExtraFields.PaymentRails,
        };
      }).map((m) => ({
        ...m,
        details: m.creditCardBrand
          ? `${creditCardMap[m.creditCardBrand]} **** ${m.details.replace(/\D/g, '').replace(/\d*(\d{4})$/, '$1')}`
          : m.type === 'PhoneNumber' || m.kind === 'PHONE' || m.kind === 'MOBILE'
            ? formatPhoneNumber(m.details)
            : m.type === 'Email'
              ? m.details.toLowerCase()
              : m.details,
      }));

      const sortedMethods = sortPaymentMethods(paymentMethods);
      const group = sortPaymentMethods(groupPaymentMethods(paymentMethods));
      setMethods(sortedMethods);
      setGroupedMethods(group);

      return sortedMethods;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  };

  function groupPaymentMethods(data: Array<PaymentMethod>) {
    type PaymentMethodBase = Omit<PaymentMethod, 'groupedItems'>;

    type GroupedPayment = PaymentMethodBase & {
      isPlaceholder?: boolean;
      isCreditCardGroupPlaceholder?: boolean;
      groupedItems?: GroupedPayment[];
    };

    const groupedPayments = {} as Record<string, GroupedPayment>; // New structure to group primary contact types: Email, PhoneNumber
    const creditCardGroups = {} as Record<string, GroupedPayment>; // New structure to group credit cards
    const otherItems: Array<GroupedPayment> = [];

    data.forEach((item) => {
      const holderName = item.accountHolderName ? item.accountHolderName.trim() : '';
      const currency = item.currency ? item.currency.trim() : 'N/A_CURRENCY';

      const isPrimaryContactType = item.type === 'Email' || item.type === 'PhoneNumber';
      const isLinkedContactType = item.type === 'PayPal' || item.type === 'Venmo' || item.type === 'Zelle';
      const isCreditCardType = item.type === 'CreditCard' || item.type === 'GooglePay' || item.type === 'ApplePay';

      if (isPrimaryContactType) {
        const key = `${item.type}-${item.details}-${holderName}-${currency}`;

        if (!groupedPayments[key]) {
          groupedPayments[key] = { ...item, groupedItems: [] };
        } else {
          if (groupedPayments[key].isPlaceholder && item.addedOn) {
            const currentGroupedItems = groupedPayments[key].groupedItems;
            groupedPayments[key] = { ...item, groupedItems: currentGroupedItems };
            delete groupedPayments[key].isPlaceholder;
          }
        }
      } else if (isLinkedContactType) {
        // Zelle aliases use 'MOBILE' where Venmo/PayPal use 'PHONE'; both
        // group under PhoneNumber. An unexpected kind falls back on the alias
        // shape rather than manufacturing an 'undefined' group/type.
        const primaryIdentifier: PaymentMethodType =
          item.kind === 'EMAIL' || (item.kind !== 'PHONE' && item.kind !== 'MOBILE' && item.details.includes('@'))
            ? 'Email'
            : 'PhoneNumber';

        const key = `${primaryIdentifier}-${item.details}-${holderName}-${currency}`;

        if (!groupedPayments[key]) {
          groupedPayments[key] = {
            accountHolderName: holderName,
            type: primaryIdentifier,
            typeLabel: primaryIdentifier === 'Email' ? 'Interac e-Transfer' : 'Interac e-Transfer',
            details: item.details,
            isDefault: false,
            token: '',
            currency: item.currency || null,
            hasQuestionAndAnswer: item.hasQuestionAndAnswer || false,
            isPlaceholder: true,
            groupedItems: [],
            addedOn: item.addedOn,
          };
        }

        groupedPayments[key]?.groupedItems?.push(item);
      } else if (isCreditCardType) {
        // Grouping for CreditCard, GooglePay, ApplePay
        const cardDetailsKey = item.details; // The last 4 digits
        const cardGroupKey = `${cardDetailsKey}-${holderName}`; // Key includes details and holderName

        if (!creditCardGroups[cardGroupKey]) {
          // Creates a "header" for the card group, preferring CreditCard as the primary type
          let primaryCardObject = null;

          if (item.type === 'CreditCard') {
            primaryCardObject = { ...item, groupedItems: [] };
          } else {
            // If the first item is GooglePay/ApplePay, creates a generic placeholder
            primaryCardObject = {
              accountHolderName: holderName,
              type: 'CreditCard',
              typeLabel: 'Credit Card',
              kind: 'HOLDER', // Generic "parent" type for card grouping
              isCreditCardGroupPlaceholder: true, // New flag for card placeholders
              details: item.details,
              isDefault: false,
              token: '',
              currency: null,
              creditCardBrand: item.creditCardBrand || null,
              expirationDate: item.expirationDate || null,
              groupedItems: [],
              addedOn: item.addedOn,
            } as GroupedPayment;
          }

          creditCardGroups[cardGroupKey] = primaryCardObject;
        } else {
          // If a group already exists, and the current item is a CreditCard and the parent is a placeholder, update the parent
          if (item.type === 'CreditCard' && creditCardGroups[cardGroupKey].isCreditCardGroupPlaceholder) {
            const currentGroupedItems = creditCardGroups[cardGroupKey].groupedItems;
            creditCardGroups[cardGroupKey] = { ...item, groupedItems: currentGroupedItems };
            delete creditCardGroups[cardGroupKey].isCreditCardGroupPlaceholder;
          }
        }

        creditCardGroups[cardGroupKey].groupedItems?.push(item);
      } else {
        // Items that don't fit into any grouping
        otherItems.push(item);
      }
    });

    const finalResult = [];

    // Adds Email/PhoneNumber groupings
    for (const key in groupedPayments) {
      const primaryItem = groupedPayments[key];

      // Logic to "ungroup" solitary PayPal/Venmo items

      if (primaryItem.isPlaceholder && primaryItem.groupedItems?.length === 1) {
        const originalLinkedItem = primaryItem.groupedItems?.[0] as GroupedPayment;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isPlaceholder, ...cleanOriginalLinkedItem } = originalLinkedItem;
        finalResult.push({ ...cleanOriginalLinkedItem, groupedItems: [] });
      } else {
        if (primaryItem.isPlaceholder && primaryItem.groupedItems?.length && primaryItem.groupedItems.length > 1) {
          // Promote the first grouped item to be the primary and remove it from the groupedItems list
          const firstGrouped = primaryItem.groupedItems.shift()!;
          const remaining = primaryItem.groupedItems;
          // Copy the first group's properties onto the primary item, keeping the remaining groupedItems
          Object.assign(primaryItem, { ...firstGrouped, groupedItems: remaining });
          delete primaryItem.isPlaceholder;
        }

        finalResult.push(primaryItem);
      }
    }

    // Adds CreditCard/GooglePay/ApplePay groupings
    for (const key in creditCardGroups) {
      const primaryCardGroup = creditCardGroups[key];

      // Removes the parent item from within groupedItems if it's the item that initiated the group
      // and there are no other items to group, or if it's a solitary placeholder.
      if (primaryCardGroup.isCreditCardGroupPlaceholder && primaryCardGroup.groupedItems?.length === 1) {
        const originalCardItem = primaryCardGroup.groupedItems[0];
        // Adds the original item (solitary GooglePay/ApplePay) to the root
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isCreditCardGroupPlaceholder, ...cleanOriginalCardItem } = originalCardItem;
        finalResult.push({ ...cleanOriginalCardItem, groupedItems: [] });
      } else {
        // If the item that initiated the group is also in groupedItems, remove it to avoid duplication.
        // This happens if the first item processed for a group is the "parent" of the group.
        primaryCardGroup.groupedItems = primaryCardGroup.groupedItems?.filter(
          (groupedItem) =>
            !(
              groupedItem.type === primaryCardGroup.type &&
              groupedItem.details === primaryCardGroup.details &&
              (groupedItem.accountHolderName || '').trim() === (primaryCardGroup.accountHolderName || '').trim() &&
              !primaryCardGroup.isCreditCardGroupPlaceholder // Do not filter if it's a placeholder, as it's not the "real" item
            ),
        );

        // Ensures the parent item is not added if it has already been handled as a grouping
        if (
          (primaryCardGroup.groupedItems?.length && primaryCardGroup.groupedItems.length > 0) ||
          !primaryCardGroup.isCreditCardGroupPlaceholder
        ) {
          // Removes the isCreditCardGroupPlaceholder flag if it's no longer a solitary placeholder
          if (
            primaryCardGroup.isCreditCardGroupPlaceholder &&
            primaryCardGroup.groupedItems?.length &&
            primaryCardGroup.groupedItems.length > 0
          ) {
            delete primaryCardGroup.isCreditCardGroupPlaceholder;
          }

          finalResult.push(primaryCardGroup);
        } else if (primaryCardGroup.isCreditCardGroupPlaceholder && primaryCardGroup.groupedItems?.length === 0) {
          // This case would be for an empty placeholder that never received items; it should not be added
        }
      }
    }

    // Adds other ungrouped items
    finalResult.push(...otherItems);

    return finalResult;
  }

  function sortPaymentMethods(arr: PaymentMethod[]) {
    // eventually we want the first (0th) entry to be the primary currency.
    const currencyOrder = currencies;
    const typeOrder: PaymentMethodType[] = ['BankAccount', 'CreditCard', 'DebitCard', 'Email', 'PhoneNumber'];

    return [...arr].sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;

      if (b.isDefault && !a.isDefault) return 1;

      // set it to CAD (eventually the primary currency) so that we can compare properly.
      const aCurrency = a.currency === null ? currencies[0] : a.currency;
      const bCurrency = b.currency === null ? currencies[0] : b.currency;

      const aaCurrencyOrder = currencyOrder.indexOf(aCurrency);
      const bbCurrencyOrder = currencyOrder.indexOf(bCurrency);

      if (aaCurrencyOrder !== bbCurrencyOrder) return aaCurrencyOrder - bbCurrencyOrder;

      return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
    });
  }

  const setDefaultMethod = (method: PaymentMethodType, accountToken: string, _accountID = accountID) => {
    if (!_accountID) {
      return Promise.resolve({
        Success: false,
        ErrorMessage: 'Account ID is required',
      });
    }

    if (method === 'BankAccount') {
      // TODO: b/e to accept ContactID as well
      return api.postSetMyClientBankAccount({
        ClientAccountID: _accountID,
        SetAsDefault: true,
        Token: accountToken,
        PortalUser: loginUserName,
      });
    }

    if (method === 'CreditCard') {
      // TODO: b/e to accept ContactID as well
      // The endpoint currently ignores ClientAccountID and scopes the default
      // by the token's own client account. Sent anyway so this branch matches
      // the others the day the backend starts honouring it.
      return api.postSetClientMyCreditCard({
        CreditCardToken: accountToken,
        SetAsDefault: true,
        ClientAccountID: _accountID,
      });
    }

    if (method === 'DebitCard') {
      // TODO: b/e to accept ContactID as well
      // Same as the credit-card branch above: ignored today, correct when the
      // backend accepts it.
      return api.postSetClientMyDebitCard({
        CardToken: accountToken,
        SetAsDefault: true,
        ClientAccountID: _accountID,
      });
    }

    if (method === 'Email' || method === 'PhoneNumber') {
      // TODO: b/e to accept ContactID as well
      return api.postSetClientEmail({
        Token: accountToken,
        ClientAccountID: _accountID,
        SetAsDefault: true,
      });
    }

    if (method === 'PayPal') {
      return api.postSetClientPayPal({
        PayPalToken: accountToken,
        ClientAccountID: _accountID,
        SetAsDefault: true,
      });
    }

    if (method === 'Venmo') {
      return api.postSetClientVenmo({
        VenmoToken: accountToken,
        ClientAccountID: _accountID,
        SetAsDefault: true,
      });
    }

    if (method === 'Zelle') {
      return api.setZelleClient({
        ZelleToken: accountToken,
        ClientAccountID: _accountID,
        SetAsDefault: true,
      });
    }

    if (method === 'GooglePay') {
      return api.postSetClientGooglePay({
        GooglePayToken: accountToken,
        ClientAccountID: _accountID,
        SetAsDefault: true,
      });
    }

    if (method === 'ApplePay') {
      return api.postSetClientApplePay({
        ApplePayToken: accountToken,
        ClientAccountID: _accountID,
        SetAsDefault: true,
      });
    }

    return Promise.resolve({
      Success: false,
      ErrorMessage: '',
    });
  };

  // the rest of the methods are added through iQ11
  const addInterac = ({
    email,
    phone,
    question,
    answer,
    recipientName,
    accountID: _accountID = accountID,
  }: AddInteracParams) => {
    return api.postAddInteracEmail({
      PortalUser: loginUserName,
      [CUSTOMER_ID_PROP]: _accountID,
      RecipientName: recipientName,
      RecipientEmailAddress: email,
      RecipientPhoneNumber: phone,
      Question: question,
      Answer: answer,
      ClientControlled: clientAccountsEnabled,
    });
  };

  return {
    methods,
    groupedMethods,
    fetchMethods,
    fetching,
    setDefaultMethod,
    deleteMethod,
    deleteGroupedMethod,
    addInterac,
  };
}
